<template>
  <!-- Focusable from script only, so choosing a section in the rail moves the keyboard there too -->
  <Card :id="id" as="section" size="large" class="team-section" tabindex="-1" :aria-labelledby="`${id}-title`">
    <View direction="column" gap="md">
      <!-- On a phone the action sits beside the title and the description takes the whole width under them -->
      <div class="header">
        <Text :id="`${id}-title`" variant="h4" as="h2" class="title">{{ title }}</Text>
        <div class="action">
          <slot name="action" />
        </div>
        <Text variant="body-medium" color="secondary" class="description">{{ description }}</Text>
      </div>

      <View direction="column" gap="sm">
        <slot />
      </View>
    </View>
  </Card>
</template>

<script setup lang="ts">
import { Card, Text, View } from '@tallo/design-system';

defineProps<{ id: string; title: string; description: string }>();
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: var(--t-spacing-md);
  row-gap: var(--t-spacing-xs);
  align-items: center;

  // A section without an action (or whose action is hidden) gives the copy the column gap back
  &:has(> .action:empty) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.title,
.description {
  grid-column: 1;
}

// A description that runs the whole width of a wide card is hard to read in one sweep
.title,
.description {
  @include desktop {
    max-width: 75%;
  }
}

.action {
  grid-column: 2;
  grid-row: 1 / span 2;
  justify-self: end;

  &:empty {
    display: none;
  }
}

@include tablet {
  .description {
    grid-column: 1 / -1;
  }

  .action {
    grid-row: 1;
  }
}

.team-section {
  scroll-margin-top: var(--t-spacing-md);

  // The focus only marks where the next Tab starts; a ring around the whole card would read as a selection
  &:focus {
    outline: none;
  }
}
</style>
